import React, { useEffect, useRef, useState } from "react"

import { makeStyles } from "@mui/styles"
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
} from "@mui/material"
import { Field, Form, Formik } from "formik"

import DeleteIcon from "@mui/icons-material/Clear"

import api from "../../services/api"
import ModalDeleteMedia from "./ModalDeleteMedia"

const useStyles = makeStyles(() => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
}))

function ModalProduct({ open, handleClose, productId }) {
    const classes = useStyles()

    const initialState = {
        name: "",
        description: "",
        price: "",
    }

    const [page, setPage] = useState("product")

    const [openModalDeleteMedia, setOpenModalDeleteMedia] = useState(false)
    const [selectedMediaToDelete, setSeletedMediaToDelete] = useState(null)

    const [product, setProduct] = useState(initialState)
    const [medias, setMedias] = useState([])
    const uploadRef = useRef(null)

    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSaveProduct = async (values) => {
        setIsSubmitting(true)

        typeof values.price === "string"
            ? (values.price = parseFloat(values?.price?.replace(",", ".")))
            : values.price

        if (productId) {
            const res = await api.put(`/products/${productId}`, values)
            setProduct(res.data)
        } else {
            const res = await api.post("/products", values)
            setProduct(res.data)
        }
        setIsSubmitting(false)
        setPage("medias")
    }

    async function getMedias() {
        const res = await api.get(`/medias?productId=${product.id}`)
        setMedias(res.data)
    }

    useEffect(() => {
        if (product.id) {
            getMedias()
        }
    }, [product.id])

    async function getProduct() {
        const res = await api.get(`/products/${productId}`)
        setProduct(res.data)
    }

    useEffect(() => {
        if (productId) {
            getProduct()
        } else {
            setProduct({
                name: "",
                description: "",
                price: "",
            })
        }
    }, [productId])

    const handleSaveMedias = async (e) => {
        setIsSubmitting(true)
        const file = e.target.files[0]
        const formData = new FormData()
        formData.append("file", file)

        formData.append("title", file.name)
        formData.append("mediaType", file.type.split("/")[0])
        formData.append("extension", file.name.split(".").pop().toLowerCase())
        formData.append("productId", product.id)
        formData.append("uploadFeatureType", "product")

        const res = await api.post("/medias", formData)
        setMedias((prevState) => [...prevState, res.data])

        uploadRef.current.value = ""
        getMedias()

        setIsSubmitting(false)
    }

    const handleDeleteMedia = async () => {
        setIsSubmitting(true)
        setOpenModalDeleteMedia(false)
        await api.delete(`/medias/${selectedMediaToDelete}`)

        setMedias(medias.filter((media) => media.id != selectedMediaToDelete))

        setSeletedMediaToDelete(null)
        setIsSubmitting(false)
    }

    const handleFinish = () => {
        setPage("product")
        handleClose()
    }

    return (
        <div className={classes.root}>
            <ModalDeleteMedia
                open={openModalDeleteMedia}
                onApprove={handleDeleteMedia}
                onCancel={() => {
                    setSeletedMediaToDelete(null)
                    setOpenModalDeleteMedia(false)
                }}
            />
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth={page === "product" ? "xs" : "md"}
                fullWidth
                scroll="paper"
            >
                <input
                    type="file"
                    multiple={false}
                    ref={uploadRef}
                    onChange={handleSaveMedias}
                    style={{ display: "none" }}
                    accept="image/*,video/*"
                />
                <DialogTitle id="form-dialog-title">
                    {productId ? `Editar produto` : `Novo produto`}
                </DialogTitle>

                {page === "product" && (
                    <Formik
                        initialValues={product}
                        enableReinitialize={true}
                        onSubmit={(values, actions) => {
                            setTimeout(() => {
                                handleSaveProduct(values)
                                actions.setSubmitting(false)
                            }, 400)
                        }}
                    >
                        {({ touched, errors, isSubmitting }) => (
                            <Form>
                                <DialogContent dividers>
                                    <div className={classes.multFieldLine}>
                                        <Field
                                            as={TextField}
                                            label="Nome"
                                            autoFocus
                                            name="name"
                                            error={
                                                touched.name &&
                                                Boolean(errors.name)
                                            }
                                            helperText={
                                                touched.name && errors.name
                                            }
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                        <Field
                                            as={TextField}
                                            label="Descrição"
                                            autoFocus
                                            name="description"
                                            error={
                                                touched.description &&
                                                Boolean(errors.description)
                                            }
                                            helperText={
                                                touched.description &&
                                                errors.description
                                            }
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            multiline
                                            maxRows={4}
                                        />
                                        <Field
                                            as={TextField}
                                            label="Preço"
                                            autoFocus
                                            name="price"
                                            error={
                                                touched.price &&
                                                Boolean(errors.price)
                                            }
                                            helperText={
                                                touched.price && errors.price
                                            }
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            placeholder="9990,90"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        R$
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </div>
                                </DialogContent>
                                <DialogActions>
                                    <Button
                                        onClick={handleClose}
                                        color="secondary"
                                        disabled={isSubmitting}
                                        variant="outlined"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        color="primary"
                                        disabled={isSubmitting}
                                        variant="contained"
                                        className={classes.btnWrapper}
                                    >
                                        Avançar
                                        {isSubmitting && (
                                            <CircularProgress
                                                size={24}
                                                className={
                                                    classes.buttonProgress
                                                }
                                            />
                                        )}
                                    </Button>
                                </DialogActions>
                            </Form>
                        )}
                    </Formik>
                )}

                {page === "medias" && (
                    <>
                        <DialogContent>
                            <Box
                                display="flex"
                                flexWrap="wrap"
                                gap={2}
                                sx={{ mt: 2 }}
                            >
                                {medias.map((media) => (
                                    <Box key={media.id} position="relative">
                                        <IconButton
                                            sx={{
                                                position: "absolute",
                                                top: -20,
                                                right: -20,
                                                zIndex: 2,
                                            }}
                                            onClick={() => {
                                                setSeletedMediaToDelete(
                                                    media.id,
                                                )
                                                setOpenModalDeleteMedia(true)
                                            }}
                                            disabled={isSubmitting}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                        {media.mediaType === "image" && (
                                            <img
                                                src={media.fileFullURL}
                                                height="150"
                                                width="180"
                                                alt={media.title}
                                                style={{
                                                    objectFit: "cover",
                                                    borderRadius: 12,
                                                    marginRight: 5,
                                                }}
                                            />
                                        )}
                                        {media.mediaType === "video" && (
                                            <video
                                                key={media.id}
                                                width="180"
                                                height="150"
                                                // muted
                                                // autoPlay
                                                controls
                                                style={{
                                                    objectFit: "cover",
                                                    borderRadius: 12,
                                                    marginRight: 5,
                                                }}
                                            >
                                                <source
                                                    src={media.fileFullURL}
                                                    type={`${media.mediaType}/${media.extension}`}
                                                />
                                            </video>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                            {medias.length <= 5 && (
                                <Button
                                    onClick={() => uploadRef.current.click()}
                                    variant="contained"
                                    sx={{ mt: 2 }}
                                    disabled={isSubmitting}
                                >
                                    Upload
                                </Button>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => setPage("product")}
                                color="secondary"
                                disabled={isSubmitting}
                                variant="outlined"
                            >
                                Voltar
                            </Button>
                            <Button
                                type="button"
                                color="primary"
                                disabled={isSubmitting}
                                variant="contained"
                                className={classes.btnWrapper}
                                onClick={handleFinish}
                            >
                                Fechar
                                {isSubmitting && (
                                    <CircularProgress
                                        size={24}
                                        className={classes.buttonProgress}
                                    />
                                )}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </div>
    )
}

export default ModalProduct
